import { useCallback, useEffect, useState } from 'react';
import { Banner, BlockStack, Button, Card, DataTable, InlineStack, Page, Spinner, Text } from '@shopify/polaris';
import { useApi } from '../hooks/useApi';
import { useMoney } from '../hooks/useMoney';

type SavedValuation = { id: number; snapshot_date: string; snapshot_type: string; total_units: number; total_cost: number; notes: string | null };
type SavedItem = { id: number; sku: string; title: string; batch_number: string | null; quantity: number; unit_cost: number | null; total_cost: number | null };
export default function ValuationSnapshots() {
  const { fetchApi, error } = useApi();
  const formatCurrency = useMoney();
  const [snapshots, setSnapshots] = useState<SavedValuation[] | null>(null);
  const [detail, setDetail] = useState<{ snapshot: SavedValuation; items: SavedItem[] } | null>(null);
  const [selected, setSelected] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setSnapshots(null); setDetail(null); setSelected(null);
    const result = await fetchApi<{ snapshots: SavedValuation[] }>('/costing/valuation/snapshots');
    if (result) setSnapshots(result.snapshots);
  }, [fetchApi]);
  useEffect(() => { void load(); }, [load]);
  const view = async (id: number) => {
    setSelected(id); setDetail(null); setBusy(true);
    const result = await fetchApi<{ snapshot: SavedValuation; items: SavedItem[] }>(`/costing/valuation/snapshots/${id}`);
    if (result) setDetail(result);
    setBusy(false);
  };
  return <Page title="Valuation snapshots" backAction={{ content: 'Stock Valuation', url: '/reports/stock-valuation' }}>
    <BlockStack gap="400">
      {error && <Banner tone="critical">{error}</Banner>}
      <InlineStack><Button onClick={() => void load()} disabled={busy}>Refresh snapshots</Button></InlineStack>
      <Card><BlockStack gap="300">
        <Text as="h2" variant="headingMd">Saved snapshots</Text>
        {!snapshots && !error && <Spinner accessibilityLabel="Loading snapshots" />}
        {snapshots?.length === 0 && <Text as="p">No snapshots saved yet. Use Create Snapshot on Stock Valuation to save one.</Text>}
        {!!snapshots?.length && <DataTable columnContentTypes={['text','text','numeric','numeric','text']} headings={['Date','Type','Units','Value','Actions']} rows={snapshots.map(snapshot => [snapshot.snapshot_date,snapshot.snapshot_type.replace(/_/g,' '),snapshot.total_units,formatCurrency(snapshot.total_cost),<Button key={snapshot.id} disabled={busy} onClick={() => void view(snapshot.id)}>{`View snapshot ${snapshot.id}`}</Button>])} footerContent="Up to 100 most recent snapshots" />}
      </BlockStack></Card>
      {selected !== null && <Card><BlockStack gap="300">
        <Text as="h2" variant="headingMd">Snapshot {selected}</Text>
        {busy && <Spinner accessibilityLabel="Loading snapshot details" />}
        {detail && <><Text as="p">{detail.snapshot.snapshot_date} · {formatCurrency(detail.snapshot.total_cost)} · {detail.snapshot.total_units} units</Text>{detail.snapshot.notes && <Text as="p">{detail.snapshot.notes}</Text>}
          {detail.items.length ? <DataTable columnContentTypes={['text','text','text','numeric','numeric','numeric']} headings={['SKU','Product','Batch','Quantity','Unit cost','Total cost']} rows={detail.items.map(item => [item.sku,item.title,item.batch_number || '—',item.quantity,item.unit_cost === null ? 'Not recorded' : formatCurrency(item.unit_cost),item.total_cost === null ? 'Not recorded' : formatCurrency(item.total_cost)])} /> : <Text as="p">This snapshot has no stock lines.</Text>}
        </>}
      </BlockStack></Card>}
    </BlockStack>
  </Page>;
}
